import React, { useEffect, useState } from 'react';
import './DeadlineDocket.scss';
import filterFactory from '../../../components/react-bootstrap-table-next/react-bootstrap-table2-filter';
import BootstrapTable from '../../../components/react-bootstrap-table-next/react-bootstrap-table2';
import paginationFactory from '../../../components/react-bootstrap-table-next/react-bootstrap-table2-paginator';
import { TimelineEvent } from '../../../slices/marketplace/timeline/models/timeline-event.model';
import { useDispatch } from 'react-redux';
import { useStateSelector } from '../../../store/selectors';
import { GetCurrentEvents } from '../../../slices/marketplace/timeline/thunks/get-current-events.thunk';
import { TableFilter } from '../../../components/Table/models/table-filter';
import Helpers from '../../../utils/helper';
import { TimelineParty } from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { MarketplaceOpportunityStatus } from '../../../slices/marketplace/opportunity/models/opportunity-status';
import { useLocation } from 'react-router-dom';
import { LinkedDataRequestProject } from '../../../slices/marketplace/timeline/models/linked-data-request-project.model';
import { useCurrentMarketplaceParty } from '../../../custom-hooks/useCurrentMarketplaceParty';
import { isEventActionClickable } from '../EventActionAvailabilityHelper';
import { EngagementData } from '../../../slices/marketplace/timeline/models/engagement-data.model';
import { TimelineActionType } from '../../../slices/marketplace/timeline/models/timeline-action-type.model';
import { NotificationFlag } from '../../../components/NotificationFlag/NotificationFlag';
import { EngagementBuyerActionState } from '../../../slices/marketplace/timeline/models/engagement-buyer-action-state.model';
import { TimelineActionStatus } from '../../../slices/marketplace/timeline/models/enums/timeline-action-status.enum';

interface DeadlineDocketProps {
    engagementId: number;
    engagementStartDate: Date;
    onEventClick(id: string): void;
    buyerActionState?: EngagementBuyerActionState[];
    events: TimelineEvent[];
    engagementData: EngagementData;
    dataRequestProjects?: LinkedDataRequestProject[];
    opportunityStatus?: MarketplaceOpportunityStatus;
    notificationFlagPrefix: string;
    isReadonly?: boolean;
}

const DeadlineDocket = (props: DeadlineDocketProps) => {
    const dispatch = useDispatch() as any;
    const location = useLocation();
    const currentTimelineEvents = useStateSelector(
        (state) => state.timelines.currentTimelineEvents
    );
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalSize, setTotalSize] = useState(1);
    const [sizePerPage, setSizePerPage] = useState(5);
    const [events, setEvents] = useState<TimelineEvent[]>([]);
    const [eventsParty, setEventsParty] = useState<TimelineParty>();

    const defaultSorted: [{ dataField: any; order: any }] = [
        {
            dataField: 'name',
            order: 'asc',
        },
    ];

    const [currentSort, setCurrentSort] = useState<TableFilter>(
        new TableFilter({ filters: [], logic: '' }, page, sizePerPage, {
            dir: 'asc',
            field: 'name',
        })
    );

    const handleTableChange = (_type: any, newState: any) => {
        const { page, sizePerPage, sortField, sortOrder } = newState;

        const updatedPostObj = new TableFilter(
            { filters: [], logic: '' },
            page,
            sizePerPage,
            { dir: sortOrder, field: sortField }
        );
        setCurrentSort(updatedPostObj);
    };

    const party = useCurrentMarketplaceParty(props.engagementId);

    const getEventsParty = (): TimelineParty => {
        if (party === TimelineParty.Buyer) {
            return TimelineParty.Buyer;
        }
        if (party === TimelineParty.VetValue) {
            return TimelineParty.VetValue;
        }
        return TimelineParty.Seller;
    };

    const fetchCurrentEvents = async () => {
        await dispatch(
            GetCurrentEvents({
                filterParams: currentSort,
                engagementId: props.engagementId,
                party: party,
            })
        );
    };

    useEffect(() => {
        setEventsParty(getEventsParty());
        fetchCurrentEvents()
            .then(() => {
                if (
                    location.state?.selectedEventId &&
                    props?.events.some(
                        (x) => x.id === location.state.selectedEventId
                    )
                ) {
                    window.scrollTo({
                        top: document.body.scrollHeight,
                        behavior: 'smooth',
                    });
                    window.history.replaceState({}, '');
                    const isClikable = isEventActionClickable(
                        location.state.selectedEventId,
                        false,
                        props.engagementData,
                        props.events,
                        party,
                        props.buyerActionState
                    );
                    if (isClikable) {
                        props.onEventClick(location.state.selectedEventId);
                    }
                }
            })
            .finally(() => setIsLoading(false));
    }, [props.engagementId]);

    useEffect(() => {
        fetchCurrentEvents();
    }, [currentSort]);

    useEffect(() => {
        const filteredEvents = currentTimelineEvents.events.filter(
            (event) =>
                (event.party === eventsParty ||
                (event.party === TimelineParty.SellerAndBuyer &&
                    (party === TimelineParty.Seller ||
                        party === TimelineParty.Buyer)) ||
                (party === TimelineParty.Broker &&
                    event.linkedAction?.actionType ===
                        TimelineActionType.VetValueBuyerSelectionAndTeaserPosting)) &&
                !(party === TimelineParty.Buyer &&
                    event.linkedAction != null &&
                    props.buyerActionState?.some(
                        (as) =>
                            as.actionId === event.linkedAction.id &&
                            as.status === TimelineActionStatus.Completed
                    ))
        );
        if (
            props.opportunityStatus !==
                MarketplaceOpportunityStatus.LostOpportunity &&
            props.opportunityStatus !==
                MarketplaceOpportunityStatus.RejectedBySeller &&
            props.opportunityStatus !==
                MarketplaceOpportunityStatus.RejectedByBuyer
        ) {
            setEvents(filteredEvents);
        }
        setPage(currentTimelineEvents.pageNumber);
        setTotalSize(currentTimelineEvents.totalItems);
        setSizePerPage(currentTimelineEvents.pageSize);
    }, [currentTimelineEvents]);

    useEffect(() => {
        fetchCurrentEvents();
    }, [props.events]);

    const getEventName = (cellContent: string, row: TimelineEvent) => {
        const project = props.dataRequestProjects
            ? props.dataRequestProjects?.find(
                  (p) => p.id == row.linkedAction?.referenceItemId
              )
            : undefined;

        const eventName = project?.actionLinkText ?? cellContent;
        const isClikable = isEventActionClickable(
            row.id,
            !!props.isReadonly,
            props.engagementData,
            props.events,
            party,
            props.buyerActionState
        );
        return (
            <div
                className={
                    'notification-flag-wrapper ' +
                    (isClikable ? 'linked-action' : '')
                }
                onClick={() => {
                    if (row.linkedAction && isClikable) {
                        props.onEventClick(row.id);
                    }
                }}>
                {eventName}
                <NotificationFlag
                    eventCode={`${props.notificationFlagPrefix}.${row.id}`}/>
            </div>
        );
    };

    const columns = [
        {
            dataField: 'dayEnd',
            text: 'Deadline',
            headerClasses: 'deadline-column-header',
            sort: true,
            formatter: (cellContent: number) => {
                return (
                    <div>
                        {calculateDateInMilliseconds(
                            props.engagementStartDate,
                            cellContent
                        ).toLocaleDateString()}
                    </div>
                );
            },
        },
        {
            dataField: 'stageName',
            text: 'Stage',
            headerClasses: 'stage-column-header',
            sort: true,
            formatter: (cellContent: string) => {
                return <div>{cellContent}</div>;
            },
        },
        {
            dataField: 'name',
            text: 'Step',
            headerClasses: 'step-column-header',
            sort: true,
            formatter: (cellContent: string, row: TimelineEvent) =>
                getEventName(cellContent, row),
        },
    ];

    const calculateDateInMilliseconds = (
        baseDate: Date,
        daysToAdd: number
    ): Date => {
        if (typeof baseDate === 'string') {
            baseDate = new Date(baseDate);
        }
        return new Date(baseDate.getTime() + daysToAdd * 24 * 60 * 60 * 1000);
    };

    return (
        <div className="deadline-docket">
            <div className="body_inner_content">
                <div className="obpbq_row valuation_section">
                    <div className="sampledetail">
                        <div className="mb-3">
                            <strong>Deadline Docket</strong>
                        </div>
                        <div
                            role="tabpanel"
                            id="panel-0"
                            aria-labelledby="tab-0"
                            aria-hidden="false">
                            <div className="card-body">
                                {isLoading ? (
                                    Helpers.renderTableLoader()
                                ) : (
                                    <BootstrapTable
                                        striped
                                        bordered
                                        bootstrap4
                                        remote
                                        keyField="id"
                                        data={events}
                                        columns={columns}
                                        defaultSorted={defaultSorted}
                                        filter={filterFactory()}
                                        pagination={paginationFactory({
                                            page,
                                            sizePerPage,
                                            totalSize,
                                            tooltipTargetId:
                                                'current-events-tooltip',
                                        })}
                                        onTableChange={handleTableChange}
                                        noDataIndication="No Events available"
                                        rowStyle={{ height: '44px' }}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DeadlineDocket;
